<script lang="ts">
  import { t } from '$core/i18n';
  import {
    buildNodeTree,
    overallProgress,
    type TreeNode
  } from '$features/checklist';
  import {
    listNodes,
    createNode,
    deleteNodeCascade,
    toggleNodeCascadeCompleted
  } from '$db/local/repositories';
  import { liveSync } from '$db/local/live.svelte';
  import type { Node } from '$domain/types';
  import { Button } from '$ui/primitives/Button';
  import { Icon } from '$ui/primitives/Icon';
  import { Badge } from '$ui/primitives/Badge';
  import { EmptyState } from '$ui/primitives/EmptyState';
  import { ProgressBar } from '$ui/primitives/ProgressBar';
  import ImportSyllabusModal from '$ui/components/ImportSyllabusModal.svelte';
  import { slide } from 'svelte/transition';
  import { toast } from '$ui/primitives/Toast';
  import type { PreferencesStore } from '$core/preferences';

  let { prefs }: { prefs: PreferencesStore } = $props();

  let rawItems = $state<Node[]>([]);
  let showAddModal = $state(false);
  let modalTab = $state<'manual' | 'import'>('manual');
  interface TreeNodeInput {
    id: string;
    title: string;
    type: string;
    children: TreeNodeInput[];
  }

  function createEmptyNode(type = ''): TreeNodeInput {
    return { id: crypto.randomUUID(), title: '', type, children: [] };
  }

  let manualRoot = $state<TreeNodeInput>(createEmptyNode('Course'));
  let manualError = $state(false);
  let isShaking = $state(false);

  async function saveManual() {
    if (!manualRoot.title.trim()) {
      manualError = true;
      isShaking = true;
      setTimeout(() => (isShaking = false), 500);
      toast.error('Please enter a name for the root node.');
      return;
    }
    manualError = false;
    await createNodeRecursive(manualRoot, null);
    showAddModal = false;
    manualRoot = createEmptyNode('Course');
  }

  async function createNodeRecursive(node: TreeNodeInput, parentId: string | null) {
    if (!node.title.trim()) return;
    const created = await createNode(parentId, node.title.trim(), node.type.trim() || (parentId ? 'topic' : 'subject'));
    for (const child of node.children) {
      await createNodeRecursive(child, created.id);
    }
  }
  let isAddingTopic = $state(false);
  let newTopicName = $state('');
  let addingChildTo = $state<TreeNode | null>(null);
  let newChildName = $state('');
  let nodeToDelete = $state<TreeNode | null>(null);
  let expandedIds = $state<Set<string>>(new Set());

  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem('sq:checklist_expanded');
    if (saved) {
      try { expandedIds = new Set(JSON.parse(saved)); } catch (e) { /* ignore */ }
    }
  }

  $effect(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('sq:checklist_expanded', JSON.stringify(Array.from(expandedIds)));
    }
  });

  let filteredItems = $derived(
    prefs.prefs.hideCompleted ? rawItems.filter(i => !i.completed) : rawItems
  );
  
  let tree = $derived(buildNodeTree(filteredItems));
  let overall = $derived(overallProgress(tree));

  liveSync(
    () => listNodes(),
    (v) => { rawItems = v; }
  );

  function isExpanded(id: string): boolean {
    return expandedIds.has(id);
  }

  function toggleExpand(id: string, event?: MouseEvent) {
    if (event) event.stopPropagation();
    const next = new Set(expandedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    expandedIds = next;
  }

  function getAllParentIds(nodes: TreeNode[]): string[] {
    let ids: string[] = [];
    for (const n of nodes) {
      if (!n.isLeaf) {
        ids.push(n.id);
        ids = ids.concat(getAllParentIds(n.children));
      }
    }
    return ids;
  }

  function expandAll() {
    expandedIds = new Set(getAllParentIds(tree));
  }

  function collapseAll() {
    expandedIds = new Set();
  }

  async function toggleNode(node: TreeNode) {
    const target = !node.completed;
    await toggleNodeCascadeCompleted(node.id, target);
  }

  async function resetAll() {
    const ids = rawItems.filter(i => !i.parentId).map((i) => i.id);
    for (const id of ids) await toggleNodeCascadeCompleted(id, false);
  }

  function typeLabel(type: string): string {
    if (type === 'subject') return 'Subject';
    if (type === 'subtopic') return 'Subtopic';
    if (type === 'book') return 'Book';
    if (type === 'chapter') return 'Chapter';
    if (type === 'section') return 'Section';
    return type;
  }

  function getNextLevel(type: string) {
    if (type === 'subject') return 'Subtopic';
    if (type === 'subtopic') return 'Book';
    if (type === 'book') return 'Chapter';
    if (type === 'chapter') return 'Section';
    return 'Item';
  }

  async function addTopic() {
    if (!newTopicName.trim()) return;
    await createNode(null, newTopicName.trim(), 'subject');
    newTopicName = '';
    isAddingTopic = false;
  }

  function startAdding(parent: TreeNode) {
    addingChildTo = parent;
    if (!isExpanded(parent.id)) {
      toggleExpand(parent.id, new MouseEvent('click'));
    }
  }

  async function saveChild(parent: TreeNode) {
    if (!newChildName.trim()) return;
    try {
      const nextType = getNextLevel(parent.type);
      await createNode(parent.id, newChildName.trim(), nextType);
      newChildName = '';
      addingChildTo = null;
    } catch (e) {
      console.error(e);
    }
  }

  function requestDeleteNode(item: TreeNode) {
    nodeToDelete = item;
  }

  async function confirmDeleteNode() {
    if (!nodeToDelete) return;
    await deleteNodeCascade(nodeToDelete.id);
    nodeToDelete = null;
  }
</script>

<section class="view">
  <header class="view-head">
    <h1 class="view-title">{t('checklist.title')}</h1>
  </header>

  <div class="overall">
    <div class="overall-head">
      <span class="overall-label">{t('checklist.overall')}</span>
      <span class="overall-value">
        {t('checklist.completedOf', {
          done: overall.completed,
          total: overall.total
        })}
      </span>
    </div>
    <ProgressBar value={overall.ratio * 100} label={t('checklist.overall')} />
  </div>

  {#if tree.length === 0}
    <EmptyState
      icon="list-checks"
      title={t('checklist.empty')}
      description={t('checklist.emptyHint')}
    />
  {:else}
    <div class="toolbar">
      <Button variant="outline" size="sm" onclick={expandAll}>
        {t('checklist.expandAll')}
      </Button>
      <Button variant="outline" size="sm" onclick={collapseAll}>
        {t('checklist.collapseAll')}
      </Button>
      <Button variant="plain" size="sm" onclick={() => void resetAll()}>
        {t('checklist.clearCompleted')}
      </Button>
      
      <label class="hide-completed-label">
        <input type="checkbox" bind:checked={prefs.prefs.hideCompleted} />
        <span>Hide completed</span>
      </label>
    </div>

    <div class="tree-container">
      <ul class="tree">
        {#snippet node(n: TreeNode)}
          <li>
            <div
              class="row"
              data-leaf={n.isLeaf || undefined}
              data-completed={n.completed || undefined}
            >
              {#if !n.isLeaf}
                <button
                  type="button"
                  class="expander"
                  onclick={(e) => toggleExpand(n.id, e)}
                  aria-label={isExpanded(n.id) ? t('common.collapse') : t('common.expand')}
                  aria-expanded={isExpanded(n.id)}
                >
                  <Icon name="chevron-right" size={16} class="chevron-icon" />
                </button>
              {:else}
                <span class="expander-spacer"></span>
              {/if}

              <button
                type="button"
                class="checkbox"
                data-checked={n.completed || undefined}
                onclick={() => void toggleNode(n)}
                aria-label={t('checklist.toggleComplete')}
                aria-pressed={n.completed}
              >
                {#if n.completed}<Icon name="check" size={12} />{/if}
              </button>

              <span class="title">{n.title}</span>

              {#if !n.isLeaf}
                <span class="progress-text">
                  {n.completedLeaves}/{n.totalLeaves}
                </span>
                <span class="progress-mini">
                  <span
                    class="progress-mini-fill"
                    style="transform: scaleX({n.progress});"
                  ></span>
                </span>
              {/if}

              <Badge variant="default" shape="square" size="sm">{typeLabel(n.type)}</Badge>

              <div class="tree-actions">
                <button title="Add {getNextLevel(n.type)}" class="add-btn" onclick={(e) => { e.stopPropagation(); startAdding(n); }}>
                  <Icon name="plus" size={14} />
                </button>
                <button title="Delete" onclick={(e) => { e.stopPropagation(); requestDeleteNode(n); }}>
                  <Icon name="trash" size={14} />
                </button>
              </div>
            </div>

            {#if addingChildTo?.id === n.id}
              <div class="add-child-input">
                <input bind:value={newChildName} placeholder="New {getNextLevel(n.type)}..." onkeydown={(e) => e.key === 'Enter' && saveChild(n)} />
                <button class="check-btn" onclick={() => saveChild(n)}><Icon name="check" size={14} /></button>
                <button class="cancel-btn" onclick={() => addingChildTo = null}><Icon name="x" size={14} /></button>
              </div>
            {/if}

            {#if !n.isLeaf && isExpanded(n.id) && n.children.length > 0}
              <ul class="children">
                {#each n.children as c (c.id)}
                  {@render node(c)}
                {/each}
              </ul>
            {/if}
          </li>
        {/snippet}

        {#each tree as root (root.id)}
          {@render node(root)}
        {/each}
      </ul>

      {#if isAddingTopic}
        <div class="add-child-input" style="margin-left: 0; padding-left: 0; margin-top: var(--space-2);">
          <input type="text" bind:value={newTopicName} placeholder="Subject name" onkeydown={(e) => e.key === 'Enter' && addTopic()} />
          <button class="check-btn" onclick={addTopic}><Icon name="check" size={14} /></button>
          <button class="cancel-btn" onclick={() => isAddingTopic = false}><Icon name="x" size={14} /></button>
        </div>
      {:else}
        <button class="add-ghost-btn" onclick={() => isAddingTopic = true} style="margin-top: var(--space-3);">
          <Icon name="plus" size={14} />
          <span>Add Subject</span>
        </button>
      {/if}
    </div>
  {/if}

  {#if nodeToDelete}
    <div class="trash-overlay" role="presentation" onclick={() => nodeToDelete = null}>
      <div class="trash-modal" role="dialog" tabindex="-1" aria-modal="true" onclick={(e) => e.stopPropagation()} onkeydown={(e) => e.key === 'Escape' && (nodeToDelete = null)}>
        <div class="trash-modal-header">
          <h3>Delete "{nodeToDelete.title}"?</h3>
          <button class="trash-close" onclick={() => nodeToDelete = null}><Icon name="x" size={16} /></button>
        </div>
        <div class="delete-warning-content">
          <p>This action will move the following to the trash:</p>
          <ul class="delete-tree-preview">
            <li><span class="level-badge">{typeLabel(nodeToDelete.type)}</span> {nodeToDelete.title}</li>
            {#if !nodeToDelete.isLeaf}
              <li class="child-indicator">...and all its nested items.</li>
            {/if}
          </ul>
        </div>
        <div class="trash-modal-actions">
          <Button variant="plain" onclick={() => nodeToDelete = null}>Cancel</Button>
          <Button variant="filled" class="danger-btn" onclick={confirmDeleteNode}>Move to Trash</Button>
        </div>
      </div>
    </div>
  {/if}

  <!-- Botón flotante inferior derecho -->
  <button class="add-syllabus-btn" onclick={() => { showAddModal = true; manualRoot = createEmptyNode('Course'); modalTab = 'manual'; manualError = false; }} title={t('checklist.addSyllabus')}>
    <Icon name="plus" size={24} />
    <span class="btn-text">{t('checklist.addSyllabus')}</span>
  </button>

  {#if showAddModal}
    <div class="trash-overlay" role="presentation" onclick={() => showAddModal = false} onkeydown={(e) => e.key === 'Escape' && (showAddModal = false)}>
      <div class="trash-modal add-modal {isShaking ? 'shake' : ''}" role="dialog" aria-modal="true" tabindex="-1" onclick={(e) => e.stopPropagation()} onkeydown={(e) => e.key === 'Escape' && (showAddModal = false)}>
        <div class="trash-modal-header">
          <h3>{t('checklist.addSyllabus')}</h3>
          <button class="trash-close" onclick={() => showAddModal = false}><Icon name="x" size={16} /></button>
        </div>
        
        <div class="modal-tabs">
          <button class:active={modalTab === 'manual'} onclick={() => modalTab = 'manual'}>{t('checklist.tabManual')}</button>
          <button class:active={modalTab === 'import'} onclick={() => modalTab = 'import'}>{t('checklist.tabImport')}</button>
        </div>

        {#key modalTab}
          <div in:slide={{ duration: 250 }}>
            {#if modalTab === 'manual'}
              <div class="manual-form">
                {#snippet InputNode(node: TreeNodeInput, depth: number, parent: TreeNodeInput | null, index: number)}
                  <div class="input-node" style="margin-left: {depth * 20}px">
                    <div class="node-inputs">
                      <input bind:value={node.title} placeholder={depth === 0 ? t('checklist.rootNamePlaceholder') : t('checklist.nodeNamePlaceholder')} />
                      <input bind:value={node.type} placeholder={depth === 0 ? t('checklist.typePlaceholder') : t('checklist.subItemTypePlaceholder')} />
                      {#if depth > 0 && parent}
                        <button class="icon-btn danger" onclick={() => parent.children.splice(index, 1)}><Icon name="trash" size={14} /></button>
                      {/if}
                    </div>
                    <div class="node-actions">
                      <button class="add-child-btn" disabled={!node.title.trim()} onclick={() => node.children.push(createEmptyNode())}>
                        <Icon name="plus" size={14}/> {t('checklist.addSubItem')}
                      </button>
                    </div>
                    {#if node.children.length > 0}
                      <div class="children-container">
                        {#each node.children as child, i}
                          {@render InputNode(child, depth + 1, node, i)}
                        {/each}
                      </div>
                    {/if}
                  </div>
                {/snippet}

                {@render InputNode(manualRoot, 0, null, 0)}

                <div class="trash-modal-actions">
                  <Button variant="plain" onclick={() => showAddModal = false}>{t('checklist.cancel')}</Button>
                  <Button variant="filled" onclick={saveManual}>{t('checklist.create')}</Button>
                </div>
                {#if manualError}
                  <p class="hint-text">{t('checklist.rootNameError')}</p>
                {/if}
              </div>
            {:else}
              <div class="import-wrapper">
                <ImportSyllabusModal onImportSuccess={() => { showAddModal = false; }} />
              </div>
            {/if}
          </div>
        {/key}
      </div>
    </div>
  {/if}
</section>

<style>
  .view { display: flex; flex-direction: column; gap: var(--space-4); width: 100%; }
  .tree-container { padding-right: var(--space-2); }
  .view-head { display: flex; justify-content: space-between; align-items: center; gap: var(--space-3); }
  .view-title { margin: 0; font-size: var(--text-h1); font-weight: var(--text-h1-weight); letter-spacing: var(--text-h1-track); line-height: var(--text-h1-lh); color: var(--color-text-primary); text-wrap: balance; }
  .overall { display: flex; flex-direction: column; gap: var(--space-2); padding: var(--space-4); background: var(--color-surface); border: var(--border-width) solid var(--color-border); border-radius: var(--radius-lg); }
  .overall-head { display: flex; justify-content: space-between; align-items: baseline; gap: var(--space-3); }
  .overall-label { font-size: var(--text-label); font-weight: var(--text-label-weight); letter-spacing: var(--text-label-track); text-transform: uppercase; color: var(--color-text-muted); }
  .overall-value { font-family: var(--font-mono); font-size: var(--text-mono-data); color: var(--color-text-primary); font-variant-numeric: tabular-nums; }
  .toolbar { display: flex; flex-wrap: wrap; gap: var(--space-2); align-items: center; }
  
  .hide-completed-label {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    cursor: pointer;
    user-select: none;
  }
  .tree { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--space-1); }
  .tree ul.children { list-style: none; margin: 0 0 0 var(--space-4); padding: 0 0 0 var(--space-2); display: flex; flex-direction: column; border-left: 1px solid var(--color-border); }
  .row { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-2) var(--space-3); margin-bottom: 2px; min-height: 40px; background: transparent; border: 1px solid transparent; border-radius: var(--radius-sm); transition: background var(--dur-fast) var(--ease-out); }
  .row:hover { background: var(--color-surface-hover); }
  .row[data-completed] .title { text-decoration: line-through; color: var(--color-text-muted); }
  .expander { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; padding: 0; color: var(--color-text-muted); background: transparent; border: none; border-radius: var(--radius-sm); cursor: pointer; flex: 0 0 auto; }
  .expander:hover { color: var(--color-text-primary); background: var(--color-surface-hover); }
  .expander[aria-expanded='true'] :global(.chevron-icon) { transform: rotate(90deg); }
  .expander :global(.chevron-icon) { transition: transform var(--dur-fast) var(--ease-out); }
  .expander-spacer { width: 24px; height: 24px; display: inline-block; flex: 0 0 auto; }
  .checkbox { display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; padding: 0; color: var(--color-accent-fg); background: var(--color-surface); border: var(--border-width) solid var(--color-border-strong); border-radius: var(--radius-xs); cursor: pointer; flex: 0 0 auto; }
  .checkbox[data-checked] { background: var(--color-success); border-color: var(--color-success); }
  .title { flex: 1; font-size: var(--text-body); font-weight: var(--weight-medium); color: var(--color-text-primary); overflow-wrap: anywhere; min-width: 0; }
  .progress-text { font-family: var(--font-mono); font-size: var(--text-caption); color: var(--color-text-muted); font-variant-numeric: tabular-nums; flex: 0 0 auto; }
  .progress-mini { width: 48px; height: 4px; background: var(--color-surface-sunken); border-radius: var(--radius-full); overflow: hidden; flex: 0 0 auto; }
  .progress-mini-fill { height: 100%; width: 100%; transform-origin: left center; background: var(--color-accent); border-radius: inherit; transition: transform var(--dur-base) var(--ease-out); }
  .tree-actions { display: flex; align-items: center; gap: 4px; opacity: 0; transition: opacity var(--dur-fast) var(--ease-out); margin-left: var(--space-2); }
  .row:hover .tree-actions { opacity: 1; }
  .tree-actions button { background: transparent; border: none; color: var(--color-text-muted); cursor: pointer; padding: 4px; border-radius: var(--radius-sm); display: flex; align-items: center; justify-content: center; }
  .tree-actions .add-btn { color: var(--color-brand); }
  .tree-actions .add-btn:hover { background: var(--color-brand-tint); }
  .tree-actions button:hover { color: var(--color-text-primary); background: var(--color-surface-hover); }
  .add-child-input { display: flex; gap: 4px; padding: 4px var(--space-4) 4px var(--space-8); margin-bottom: 4px; }
  .add-child-input input { flex: 1; padding: 6px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: var(--color-surface); color: var(--color-text-primary); font-size: var(--text-sm); }
  .add-child-input button { padding: 6px; border-radius: var(--radius-sm); border: none; background: transparent; cursor: pointer; color: var(--color-text-muted); }
  .add-child-input .check-btn:hover { color: var(--color-success); }
  .add-child-input .cancel-btn:hover { color: var(--color-danger); }
  .add-ghost-btn { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-2); border-radius: var(--radius-sm); border: 1px dashed var(--color-border-strong); color: var(--color-text-muted); background: transparent; cursor: pointer; font-size: var(--text-xs); font-weight: 500; width: 100%; transition: border-color var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out); }
  .add-ghost-btn:hover { border-color: var(--color-brand); color: var(--color-brand); }
  .trash-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); z-index: var(--z-modal, 50); display: flex; align-items: center; justify-content: center; padding: var(--space-4); }
  .trash-modal { background: var(--color-surface); border: var(--border-width) solid var(--color-border); border-radius: var(--radius-lg); padding: var(--space-5); max-width: 480px; width: 100%; display: flex; flex-direction: column; gap: var(--space-3); box-shadow: var(--shadow-lg); }
  .trash-modal-header { display: flex; justify-content: space-between; align-items: center; }
  .trash-modal-header h3 { margin: 0; font-size: 1.125rem; font-weight: 700; }
  .trash-close { background: transparent; border: none; color: var(--color-text-muted); cursor: pointer; padding: 4px; }
  .trash-close:hover { color: var(--color-text-primary); }
  .delete-warning-content { display: flex; flex-direction: column; gap: var(--space-3); color: var(--color-text-secondary); font-size: var(--text-sm); }
  .delete-tree-preview { list-style: none; padding: var(--space-3); margin: 0; background: var(--color-surface-sunken); border-radius: var(--radius-sm); border: 1px dashed var(--color-border-strong); }
  .delete-tree-preview li { display: flex; align-items: center; gap: var(--space-2); }
  .delete-tree-preview .child-indicator { color: var(--color-text-muted); font-style: italic; margin-top: var(--space-2); font-size: var(--text-xs); }
  .trash-modal-actions { display: flex; justify-content: flex-end; gap: var(--space-2); margin-top: var(--space-3); }
  
  .add-syllabus-btn {
    position: fixed;
    bottom: var(--space-8);
    right: var(--space-8);
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 56px;
    height: 56px;
    padding: 0 16px;
    background: var(--color-surface-sunken);
    color: var(--color-text-muted);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-full);
    cursor: pointer;
    box-shadow: var(--shadow-md);
    overflow: hidden;
    transition: width var(--dur-base) var(--ease-out), 
                color var(--dur-fast) var(--ease-out), 
                border-color var(--dur-fast) var(--ease-out), 
                transform var(--dur-fast) var(--ease-out), 
                box-shadow var(--dur-fast) var(--ease-out);
    z-index: var(--z-dropdown);
  }
  .add-syllabus-btn .btn-text {
    max-width: 0;
    opacity: 0;
    white-space: nowrap;
    font-weight: 600;
    font-size: var(--text-sm);
    transition: max-width var(--dur-base) var(--ease-out), 
                opacity var(--dur-fast) var(--ease-out), 
                margin-left var(--dur-base) var(--ease-out);
  }
  .add-syllabus-btn:hover {
    width: 180px; /* Ancho expandido para mostrar el texto */
    color: var(--color-brand); /* Acentúa el ícono y el texto con el color dominante */
    border-color: var(--color-brand);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
  .add-syllabus-btn:hover .btn-text {
    max-width: 140px;
    opacity: 1;
    margin-left: 8px;
  }

  .add-modal { max-width: 600px; max-height: 90vh; overflow-y: auto; }
  .modal-tabs { display: flex; border-bottom: 1px solid var(--color-border); margin-bottom: var(--space-4); }
  .modal-tabs button { flex: 1; padding: var(--space-2); background: transparent; border: none; border-bottom: 2px solid transparent; cursor: pointer; font-weight: 600; color: var(--color-text-muted); transition: color var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out); }
  .modal-tabs button.active { border-color: var(--color-brand); color: var(--color-brand); }
  .manual-form { display: flex; flex-direction: column; gap: var(--space-4); }
  .manual-form input { padding: var(--space-2); border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: var(--color-surface); color: var(--color-text-primary); font-size: var(--text-sm); width: 100%; box-sizing: border-box; }
  .add-child-btn { display: flex; align-items: center; gap: 4px; background: transparent; border: none; color: var(--color-brand); cursor: pointer; font-size: var(--text-sm); font-weight: 600; padding: var(--space-2) 0; }
  .add-child-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .import-wrapper { display: flex; flex-direction: column; margin-top: calc(var(--space-3) * -1); }
  
  /* Nuevos estilos para el constructor de árbol recursivo */
  .input-node {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-2);
    border-left: 2px solid var(--color-border);
    margin-bottom: var(--space-2);
  }
  .input-node[style*="margin-left: 0"] {
    border-left: none;
  }
  .node-inputs {
    display: flex;
    gap: var(--space-2);
    align-items: center;
  }
  .node-inputs input {
    flex: 1;
  }
  .node-actions {
    display: flex;
    gap: var(--space-2);
  }
  .children-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .hint-text {
    font-size: var(--text-xs);
    color: var(--color-warning);
    margin: 0;
    text-align: center;
  }
  .icon-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .icon-btn.danger {
    color: var(--color-danger);
  }
  .icon-btn.danger:hover {
    background: var(--color-danger-tint);
  }

  /* Animación Shake para validación */
  .shake {
    animation: shake 0.4s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  }
  @keyframes shake {
    10%, 90% { transform: translate3d(-1px, 0, 0); }
    20%, 80% { transform: translate3d(2px, 0, 0); }
    30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
    40%, 60% { transform: translate3d(4px, 0, 0); }
  }
</style>